<template>
  <div :class="['flex flex-col h-full transition-all duration-300', isCollapsed ? 'w-16' : 'w-64']">
    <!-- 折叠按钮 -->
    <div class="p-2 border-b border-gray-700 flex justify-end">
      <button @click="toggleSidebar" class="p-2 rounded hover:bg-gray-700 transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>

    <div v-if="!isCollapsed" class="flex-1 overflow-y-auto bg-gray-800">
    <!-- 用户信息 -->
    <div class="p-4 border-b border-gray-700">
      <div class="flex items-center space-x-3">
        <div class="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center">
          <span class="text-sm font-bold">{{ userInitial }}</span>
        </div>
        <div>
          <p class="text-sm font-medium">{{ authStore.user?.name }}</p>
          <p class="text-xs text-gray-400">账号: {{ authStore.user?.account }}</p>
        </div>
      </div>
    </div>

    <!-- 团队选择 -->
    <div class="p-4 border-b border-gray-700">
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-sm font-semibold text-gray-300">我的团队</h3>
        <button
          @click="showCreateTeam = true"
          class="text-indigo-400 hover:text-indigo-300 text-sm"
        >
          创建
        </button>
      </div>
      
      <div class="space-y-1">
        <div
          v-for="team in authStore.teams"
          :key="team._id"
          @click="selectTeam(team)"
          :class="[
            'p-2 rounded cursor-pointer text-sm',
            authStore.currentTeam?._id === team._id
              ? 'bg-indigo-600 text-white'
              : 'hover:bg-gray-700 text-gray-300'
          ]"
        >
          {{ team.name }}
        </div>
      </div>

      <div v-if="authStore.teams.length === 0" class="text-xs text-gray-400 mt-2">
        暂无团队，请创建或加入团队
      </div>
    </div>

    <!-- 加入团队 -->
    <div class="p-4 border-b border-gray-700">
      <h3 class="text-sm font-semibold text-gray-300 mb-2">加入团队</h3>
      <div class="flex space-x-2">
        <input
          v-model="inviteCode"
          type="text"
          placeholder="邀请码"
          class="flex-1 px-2 py-1 text-sm bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
          maxlength="6"
        />
        <button
          @click="joinTeam"
          :disabled="!inviteCode || authStore.loading"
          class="px-2 py-1 text-sm bg-indigo-600 hover:bg-indigo-700 rounded disabled:opacity-50"
        >
          加入
        </button>
      </div>
    </div>

    <!-- 团队信息 -->
    <div v-if="authStore.currentTeam" class="p-4 flex-1">
      <div class="mb-4">
        <h3 class="text-sm font-semibold text-gray-300 mb-2">团队信息</h3>
        <div class="text-xs text-gray-400 space-y-1">
          <p>名称: {{ authStore.currentTeam.name }}</p>
          <p>邀请码: <span class="font-mono bg-gray-700 px-1 rounded">{{ authStore.currentTeam.inviteCode }}</span></p>
          <p>成员: {{ authStore.currentTeam.members?.length || 1 }}人</p>
        </div>
      </div>
      
      <button
        @click="goToTeamManagement"
        class="w-full px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded text-white"
      >
        团队管理
      </button>
    </div>

    <!-- 登出按钮 -->
    <div class="p-4 border-t border-gray-700">
      <button
        @click="authStore.logout"
        class="w-full px-4 py-2 text-sm bg-red-600 hover:bg-red-700 rounded"
      >
        退出登录
      </button>
    </div>

    <!-- 创建团队对话框 -->
    <div v-if="showCreateTeam" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white p-6 rounded-lg w-80">
        <h3 class="text-lg font-semibold mb-4 text-gray-900">创建新团队</h3>
        <input
          v-model="newTeamName"
          type="text"
          placeholder="团队名称"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-4 text-gray-900"
        />
        <textarea
          v-model="newTeamDescription"
          placeholder="团队描述（可选）"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-4 resize-none text-gray-900"
          rows="3"
        ></textarea>
        <div class="flex space-x-2">
          <button
            @click="createTeam"
            :disabled="!newTeamName || authStore.loading"
            class="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {{ authStore.loading ? '创建中...' : '创建' }}
          </button>
          <button
            @click="showCreateTeam = false"
            class="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
          >
            取消
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useRouter } from 'vue-router';

const authStore = useAuthStore();
const router = useRouter();

const isCollapsed = ref(true);
const toggleSidebar = () => {
  isCollapsed.value = !isCollapsed.value;
};

const inviteCode = ref('');
const showCreateTeam = ref(false);
const newTeamName = ref('');
const newTeamDescription = ref('');

const userInitial = computed(() => {
  return authStore.user?.name ? authStore.user.name.charAt(0).toUpperCase() : 'U';
});

const selectTeam = (team) => {
  authStore.switchTeam(team);
};

const joinTeam = async () => {
  if (!inviteCode.value) return;
  
  try {
    await authStore.joinTeam(inviteCode.value);
    inviteCode.value = '';
  } catch (error) {
    console.error('加入团队失败:', error);
  }
};

const createTeam = async () => {
  if (!newTeamName.value) return;
  
  try {
    await authStore.createTeam({
      name: newTeamName.value,
      description: newTeamDescription.value
    });
    
    showCreateTeam.value = false;
    newTeamName.value = '';
    newTeamDescription.value = '';
  } catch (error) {
    console.error('创建团队失败:', error);
  }
};

const goToTeamManagement = () => {
  router.push('/teams');
};
</script>